import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ReceptionApiAdapter } from '../adapters/ReceptionApiAdapter';
import { ReceptionFilters } from '../models/Reception';
import { ReceptionService } from '../services/ReceptionService';

/**
 * Hook para obtener lista paginada de recepciones con infinite scroll
 */
export const useReceptions = (filters: ReceptionFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ReceptionApiAdapter(httpClient);
  const receptionService = new ReceptionService(apiAdapter);

  // Configurar ordenamiento por defecto: descendente por fecha de creación
  const defaultFilters: ReceptionFilters = {
    sort_column: 'created_at',
    sort_direction: 'desc',
    per_page: 25,
    ...filters,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['receptions', defaultFilters],
    queryFn: ({ pageParam = 1 }) =>
      receptionService.getReceptions({
        ...defaultFilters,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  const receptions = data?.pages.flatMap((page) => page.receptions) ?? [];
  const totalFromApi = data?.pages[data.pages.length - 1]?.total ?? 0;

  return {
    receptions,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    totalReceptions: receptions.length,
    total: totalFromApi,
  };
};

/**
 * Hook para obtener todas las recepciones (sin paginación)
 */
export const useReceptionsSimple = (filters: ReceptionFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ReceptionApiAdapter(httpClient);
  const receptionService = new ReceptionService(apiAdapter);

  // Configurar ordenamiento por defecto
  const defaultFilters: ReceptionFilters = {
    sort_column: 'created_at',
    sort_direction: 'desc',
    ...filters,
  };

  const {
    data: receptions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['receptions-simple', defaultFilters],
    queryFn: () => receptionService.getAllReceptions(defaultFilters),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    receptions,
    isLoading,
    error,
  };
};

